import React, { useEffect, useRef, useState } from "react";
import { X, CheckCircle2, XCircle, Loader2, FolderInput, Inbox, Shuffle } from "lucide-react";
import { api } from "@/lib/api";

// ---------------------------------------------------------------- shared UI
export function Modal({ title, subtitle, onClose, children, testId }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid={testId}>
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="relative z-10 w-full max-w-lg bg-zinc-950 border border-zinc-800 rounded-md shadow-2xl max-h-[85vh] overflow-y-auto">
        <div className="flex items-start justify-between px-5 py-4 border-b border-zinc-800">
          <div>
            <div className="font-display text-lg font-bold">{title}</div>
            {subtitle && <div className="text-xs text-zinc-500 mt-0.5">{subtitle}</div>}
          </div>
          <button onClick={onClose} data-testid="bulk-modal-close" className="text-zinc-500 hover:text-white"><X size={18} /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function ResultLog({ results }) {
  if (!results?.length) return null;
  return (
    <div className="mt-4 border border-zinc-800 rounded-sm divide-y divide-zinc-800 max-h-52 overflow-y-auto" data-testid="bulk-result-log">
      {results.map((r, i) => {
        const ok = r.ok !== false;
        const detail = r.error || r.reason
          || (r.records_verified != null ? `${r.records_verified} DNS verified` : "")
          || (r.key_set ? "key set" : "");
        return (
          <div key={r.site_id || r.domain || `row-${i}`} className="flex items-center gap-2 px-3 py-1.5 text-xs">
            {ok ? <CheckCircle2 size={13} className="text-emerald-400 shrink-0" /> : <XCircle size={13} className="text-rose-400 shrink-0" />}
            <span className="truncate flex-1 text-zinc-200">{r.name || r.domain || r.site_id}</span>
            <span className="text-zinc-500 truncate max-w-[45%]">{detail}</span>
          </div>
        );
      })}
    </div>
  );
}

const btnPrimary = "inline-flex items-center justify-center gap-1.5 bg-amber-500/20 border border-amber-500/40 text-amber-200 hover:bg-amber-500/30 text-xs font-semibold uppercase tracking-wider px-3 py-2 rounded-sm disabled:opacity-40";
const btnGhost = "inline-flex items-center gap-1.5 border border-zinc-700 hover:border-zinc-500 text-zinc-300 text-xs font-semibold uppercase tracking-wider px-3 py-2 rounded-sm disabled:opacity-40";
const inputCls = "w-full bg-transparent border border-zinc-800 px-3 py-2 rounded-sm text-sm";

// ---------------------------------------------------------- Move to folder
export function MoveToFolderModal({ folders, siteIds, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  async function move(folderId) {
    setBusy(true); setErr("");
    try {
      if (folderId === "__unfile__") await api.post("/folders/unassign", { site_ids: siteIds });
      else await api.post(`/folders/${folderId}/assign`, { site_ids: siteIds });
      await onDone();
      onClose();
    } catch (e) { setErr(e?.response?.data?.detail || "Move failed"); }
    finally { setBusy(false); }
  }
  return (
    <Modal testId="bulk-move-modal" title="Move to folder" subtitle={`${siteIds.length} site${siteIds.length === 1 ? "" : "s"} selected`} onClose={onClose}>
      <div className="space-y-1.5">
        {folders.map((f) => (
          <button
            key={f.id}
            data-testid={`bulk-move-to-${f.id}`}
            disabled={busy}
            onClick={() => move(f.id)}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-sm text-sm text-zinc-200 hover:bg-zinc-800/70 border border-zinc-800 disabled:opacity-40"
          >
            <span className="dot" style={{ background: f.color }} />
            <span className="flex-1 text-left truncate">{f.name}</span>
            <FolderInput size={14} className="text-zinc-500" />
          </button>
        ))}
        <button
          data-testid="bulk-move-unfile"
          disabled={busy}
          onClick={() => move("__unfile__")}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-sm text-sm text-zinc-300 hover:bg-zinc-800/70 border border-dashed border-zinc-800 disabled:opacity-40"
        >
          <Inbox size={14} className="text-zinc-500" />
          <span className="flex-1 text-left">Unfile (remove from folder)</span>
        </button>
        {folders.length === 0 && <div className="text-xs text-zinc-500 pt-1">No folders yet — create one from the left rail first.</div>}
      </div>
      {err && <div data-testid="bulk-move-error" className="text-xs text-rose-400 mt-3">{err}</div>}
    </Modal>
  );
}

// ---------------------------------------------------------- SparkPost key
export function SparkPostKeyModal({ siteIds, onClose, onDone }) {
  const [apiKey, setApiKey] = useState("");
  const [sub, setSub] = useState("");
  const [runDns, setRunDns] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [results, setResults] = useState(null);
  const [job, setJob] = useState(null);
  const [err, setErr] = useState("");
  const poll = useRef(null);
  useEffect(() => () => poll.current && clearInterval(poll.current), []);

  function body(dry) {
    return {
      site_ids: siteIds, api_key: apiKey.trim(),
      subaccount_id: sub.trim() ? Number(sub.trim()) : null,
      run_dns_matrix: runDns, dry_run: dry,
    };
  }
  async function doPreview() {
    setBusy(true); setErr(""); setResults(null);
    try {
      const r = await api.post("/bulk/sparkpost-key", body(true));
      setPreview(r.data);
    } catch (e) { setErr(e?.response?.data?.detail || "Preview failed"); }
    finally { setBusy(false); }
  }
  async function apply() {
    setBusy(true); setErr("");
    try {
      const r = await api.post("/bulk/sparkpost-key", body(false));
      setResults(r.data.results || []);
      await onDone();
      if (r.data.job_id) startPolling(r.data.job_id);
    } catch (e) { setErr(e?.response?.data?.detail || "Apply failed"); }
    finally { setBusy(false); }
  }
  function startPolling(jobId) {
    poll.current = setInterval(async () => {
      try {
        const j = await api.get(`/bulk/jobs/${jobId}`);
        setJob(j.data);
        if (j.data.status === "done") { clearInterval(poll.current); poll.current = null; }
      } catch { clearInterval(poll.current); poll.current = null; }
    }, 3000);
  }
  return (
    <Modal testId="bulk-sparkpost-modal" title="Set SparkPost key" subtitle={`${siteIds.length} site${siteIds.length === 1 ? "" : "s"} selected`} onClose={onClose}>
      <div className="space-y-3">
        <div>
          <label className="label-tiny">API key</label>
          <input data-testid="bulk-sp-key" type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder="SparkPost API key" className={inputCls} />
        </div>
        <div>
          <label className="label-tiny">Subaccount ID (optional)</label>
          <input data-testid="bulk-sp-sub" value={sub} onChange={(e) => setSub(e.target.value)} placeholder="leave blank for master account" className={inputCls} />
        </div>
        <label className="flex items-center gap-2 cursor-pointer text-sm">
          <input data-testid="bulk-sp-rundns" type="checkbox" checked={runDns} onChange={(e) => setRunDns(e.target.checked)} />
          Also create domains + write &amp; verify DNS in Cloudflare (runs in background per domain)
        </label>
        <div className="flex items-center gap-2 pt-1">
          <button data-testid="bulk-sp-preview" onClick={doPreview} disabled={busy || !apiKey.trim()} className={btnGhost}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Preview
          </button>
          <button data-testid="bulk-sp-apply" onClick={apply} disabled={busy || !apiKey.trim()} className={btnPrimary}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Apply to {siteIds.length}
          </button>
        </div>

        {preview && !results && (
          <div data-testid="bulk-sp-preview-out" className="text-xs text-zinc-400 border border-zinc-800 rounded-sm p-3">
            Will set the key on <b className="text-zinc-200">{preview.count}</b> site(s).
            {runDns && <> DNS onboarding eligible on <b className="text-zinc-200">{preview.dns_eligible}</b> (need a domain + Cloudflare zone).</>}
          </div>
        )}
        <ResultLog results={results} />
        {job && (
          <div data-testid="bulk-sp-job" className="mt-3 text-xs">
            <div className="flex items-center gap-2 text-zinc-300">
              {job.status !== "done" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />}
              DNS onboarding {job.status === "done" ? "complete" : "running"} — {job.done}/{job.total}
            </div>
            <ResultLog results={job.results} />
          </div>
        )}
        {err && <div data-testid="bulk-sp-error" className="text-xs text-rose-400">{err}</div>}
      </div>
    </Modal>
  );
}

// ------------------------------------------------------- Google Workspace
export function GoogleWorkspaceModal({ siteIds, onClose, onDone }) {
  const [mode, setMode] = useState("alias");
  const [inbound, setInbound] = useState("workspace");
  const [mailCname, setMailCname] = useState(true);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [job, setJob] = useState(null);
  const [skipped, setSkipped] = useState([]);
  const [err, setErr] = useState("");
  const poll = useRef(null);
  useEffect(() => () => poll.current && clearInterval(poll.current), []);

  const body = (dry) => ({ site_ids: siteIds, mode, inbound, mail_cname: mailCname, dry_run: dry });

  async function doPreview() {
    setBusy(true); setErr("");
    try { const r = await api.post("/bulk/gws-connect", body(true)); setPreview(r.data); }
    catch (e) { setErr(e?.response?.data?.detail || "Preview failed"); }
    finally { setBusy(false); }
  }
  async function apply() {
    if (!window.confirm(`Add ${siteIds.length} domain(s) to Google Workspace as ${mode === "alias" ? "domain aliases" : "secondary domains"}${inbound === "workspace" ? " and replace their MX with Google's" : ""}?`)) return;
    setBusy(true); setErr("");
    try {
      const r = await api.post("/bulk/gws-connect", body(false));
      setSkipped(r.data.skipped || []);
      if (r.data.job_id) startPolling(r.data.job_id);
    } catch (e) { setErr(e?.response?.data?.detail || "Apply failed"); }
    finally { setBusy(false); }
  }
  function startPolling(jobId) {
    poll.current = setInterval(async () => {
      try {
        const j = await api.get(`/bulk/jobs/${jobId}`);
        setJob(j.data);
        if (j.data.status === "done") { clearInterval(poll.current); poll.current = null; onDone?.(); }
      } catch { clearInterval(poll.current); poll.current = null; }
    }, 3000);
  }

  return (
    <Modal testId="bulk-gws-modal" title="Connect Google Workspace" subtitle={`${siteIds.length} site${siteIds.length === 1 ? "" : "s"} · adds each domain to your Workspace and writes verification TXT, MX, SPF, DMARC + Gmail shortcut into Cloudflare`} onClose={onClose}>
      <div className="space-y-3">
        <div>
          <label className="label-tiny">Join Workspace as</label>
          <select data-testid="bulk-gws-mode" value={mode} onChange={(e) => { setMode(e.target.value); setPreview(null); }} className={inputCls}>
            <option value="alias">Domain alias — existing users get @domain addresses (no licences)</option>
            <option value="secondary">Secondary domain — separate users (licence each)</option>
          </select>
        </div>
        <div>
          <label className="label-tiny">Inbound mail</label>
          <select data-testid="bulk-gws-inbound" value={inbound} onChange={(e) => { setInbound(e.target.value); setPreview(null); }} className={inputCls}>
            <option value="workspace">Gmail receives everything — replaces Cloudflare Email Routing MX</option>
            <option value="sending_only">Sending-only — keep Cloudflare Email Routing inbound</option>
          </select>
        </div>
        <label className="flex items-center gap-2 cursor-pointer text-sm">
          <input data-testid="bulk-gws-mailcname" type="checkbox" checked={mailCname} onChange={(e) => setMailCname(e.target.checked)} />
          Add <span className="mono">mail.</span> → Gmail shortcut CNAME
        </label>
        <div className="text-[11px] text-zinc-500">DKIM still needs one paste per domain from the Admin console (Google has no DKIM API) — do it later from each site&apos;s Email tab.</div>

        <div className="flex items-center gap-2 pt-1">
          <button data-testid="bulk-gws-preview" onClick={doPreview} disabled={busy} className={btnGhost}>{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Preview</button>
          <button data-testid="bulk-gws-apply" onClick={apply} disabled={busy || !!job || (preview && (!preview.probe?.ok || preview.eligible === 0))} className={btnPrimary}>{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Connect {siteIds.length}</button>
        </div>

        {preview && !job && (
          <div data-testid="bulk-gws-preview-out" className="text-xs text-zinc-400 border border-zinc-800 rounded-sm p-3 space-y-1">
            <div>Workspace: {preview.probe?.ok ? <span className="text-emerald-400">connected · primary {preview.probe.primary_domain}</span> : <span className="text-rose-400">{preview.probe?.error || "not reachable"}</span>}</div>
            <div><b className="text-zinc-200">{preview.eligible}</b> of {preview.count} site(s) eligible (need a domain + Cloudflare zone){(preview.targets || []).some((t) => t.already_connected) ? ` · ${(preview.targets || []).filter((t) => t.already_connected).length} already connected (will be re-synced)` : ""}</div>
            {(preview.targets || []).filter((t) => !t.eligible).map((t) => <div key={t.site_id} className="text-zinc-500">skip {t.name || t.domain || t.site_id}: {t.reason}</div>)}
          </div>
        )}
        {job && (
          <div data-testid="bulk-gws-job" className="mt-3 text-xs">
            <div className="flex items-center gap-2 text-zinc-300">
              {job.status !== "done" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />}
              Workspace onboarding {job.status === "done" ? "complete" : "running"} — {job.done}/{job.total}
            </div>
            <ResultLog results={[...(job.results || []), ...skipped.map((s) => ({ ...s, ok: false, error: s.reason }))]} />
          </div>
        )}
        {err && <div data-testid="bulk-gws-error" className="text-xs text-rose-400">{err}</div>}
      </div>
    </Modal>
  );
}

// ------------------------------------------------------- Content settings
const CONTENT_FIELDS = [
  { key: "ai_budget_daily_usd", label: "AI budget / day (USD)", type: "number", step: "0.05", ph: "0.50", default: 0.5 },
  { key: "retention_hot_days", label: "Hot retention (days)", type: "number", ph: "14", default: 14 },
  { key: "retention_archive_days", label: "Archive retention (days)", type: "number", ph: "14", default: 14 },
  { key: "ingest_frequency", label: "Ingest cadence", type: "select", options: ["breaking", "hourly", "standard", "slow", "daily", "manual"], default: "standard" },
  { key: "auto_publish_enabled", label: "Auto-publish", type: "bool", default: true },
  { key: "auto_publish_min_quality_score", label: "Auto-publish min quality (0–1)", type: "number", step: "0.05", ph: "0.7", default: 0.7 },
  { key: "auto_publish_daily_cap", label: "Auto-publish daily cap", type: "number", ph: "10", default: 10 },
  { key: "image_policy", label: "Article images (network = inherit default)", type: "select", options: ["network", "stock", "card", "source"], default: "network" },
];

// Pre-populate every field's value with its default so a TICKED field always
// sends the value shown in the input — not just fields the operator retyped.
const DEFAULT_CONTENT_VALS = Object.fromEntries(CONTENT_FIELDS.map((f) => [f.key, f.default]));

export function ContentSettingsModal({ siteIds, onClose, onDone }) {
  const [incl, setIncl] = useState({});
  const [vals, setVals] = useState(DEFAULT_CONTENT_VALS);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [applied, setApplied] = useState(null);
  const [err, setErr] = useState("");

  function settings() {
    const out = {};
    for (const f of CONTENT_FIELDS) {
      if (!incl[f.key]) continue;
      if (f.type === "bool") out[f.key] = !!vals[f.key];
      else if (f.type === "select") out[f.key] = vals[f.key] || f.default;
      else {
        // number — always send the shown value; fall back to default if blanked
        const v = vals[f.key];
        out[f.key] = (v === "" || v === null || v === undefined) ? f.default : v;
      }
    }
    return out;
  }
  const chosen = Object.keys(settings());
  async function run(dry) {
    setBusy(true); setErr("");
    try {
      const r = await api.post("/bulk/content-settings", { site_ids: siteIds, settings: settings(), dry_run: dry });
      if (dry) { setPreview(r.data); setApplied(null); }
      else { setApplied(r.data); setPreview(null); await onDone(); }
    } catch (e) { setErr(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  }
  return (
    <Modal testId="bulk-content-modal" title="Content settings" subtitle={`Apply to ${siteIds.length} site${siteIds.length === 1 ? "" : "s"} — tick a field to include it`} onClose={onClose}>
      <div className="space-y-2.5">
        {CONTENT_FIELDS.map((f) => (
          <div key={f.key} className="flex items-center gap-3">
            <input
              data-testid={`bulk-content-incl-${f.key}`}
              type="checkbox"
              checked={!!incl[f.key]}
              onChange={(e) => setIncl((s) => ({ ...s, [f.key]: e.target.checked }))}
            />
            <label className="text-sm text-zinc-300 flex-1">{f.label}</label>
            {f.type === "bool" ? (
              <select
                data-testid={`bulk-content-val-${f.key}`}
                disabled={!incl[f.key]}
                value={vals[f.key] ? "on" : "off"}
                onChange={(e) => setVals((s) => ({ ...s, [f.key]: e.target.value === "on" }))}
                className="bg-transparent border border-zinc-800 px-2 py-1 rounded-sm text-sm w-28 disabled:opacity-40"
              >
                <option value="on">ON</option>
                <option value="off">OFF</option>
              </select>
            ) : f.type === "select" ? (
              <select
                data-testid={`bulk-content-val-${f.key}`}
                disabled={!incl[f.key]}
                value={vals[f.key] || f.options[0]}
                onChange={(e) => setVals((s) => ({ ...s, [f.key]: e.target.value }))}
                className="bg-transparent border border-zinc-800 px-2 py-1 rounded-sm text-sm w-28 disabled:opacity-40"
              >
                {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : (
              <input
                data-testid={`bulk-content-val-${f.key}`}
                type="number"
                step={f.step || "1"}
                disabled={!incl[f.key]}
                value={vals[f.key] ?? ""}
                onChange={(e) => setVals((s) => ({ ...s, [f.key]: e.target.value }))}
                placeholder={f.ph}
                className="bg-transparent border border-zinc-800 px-2 py-1 rounded-sm text-sm w-28 disabled:opacity-40"
              />
            )}
          </div>
        ))}
        <div className="flex items-center gap-2 pt-2">
          <button data-testid="bulk-content-preview" onClick={() => run(true)} disabled={busy || !chosen.length} className={btnGhost}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Preview
          </button>
          <button data-testid="bulk-content-apply" onClick={() => run(false)} disabled={busy || !chosen.length} className={btnPrimary}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Apply to {siteIds.length}
          </button>
        </div>
        {preview && (
          <div data-testid="bulk-content-preview-out" className="text-xs text-zinc-400 border border-zinc-800 rounded-sm p-3 break-words">
            Will set on {preview.count} site(s): <span className="mono text-zinc-200 break-all">{JSON.stringify(preview.will_set)}</span>
          </div>
        )}
        {applied && (
          <div data-testid="bulk-content-applied" className="text-xs text-emerald-400 border border-emerald-800 rounded-sm p-3 break-words">
            ✓ Updated {applied.modified} site(s): <span className="mono break-all">{JSON.stringify(applied.applied)}</span>
          </div>
        )}
        {err && <div data-testid="bulk-content-error" className="text-xs text-rose-400">{err}</div>}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------- Email rebind
export function EmailRebindModal({ siteIds, onClose, onDone }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get("/email/admin-destination").then((r) => setEmail(r.data?.email || "")).catch(() => {});
  }, []);
  async function run(dry) {
    setBusy(true); setErr("");
    try {
      const r = await api.post("/email/rebind-all", { email: email.trim() || null, site_ids: siteIds, dry_run: dry });
      if (dry) { setPreview(r.data); setResult(null); }
      else { setResult(r.data); setPreview(null); await onDone(); }
    } catch (e) { setErr(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  }
  return (
    <Modal testId="bulk-email-modal" title="Rebind email forwarding" subtitle={`Scoped to ${siteIds.length} selected site${siteIds.length === 1 ? "" : "s"} with a Cloudflare zone`} onClose={onClose}>
      <div className="space-y-3">
        <div>
          <label className="label-tiny">Forward all mail to</label>
          <input data-testid="bulk-email-dest" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="processing@example.com" className={inputCls} />
          <div className="text-[11px] text-zinc-500 mt-1">Must be a verified Cloudflare destination. Leave blank to use the current admin destination.</div>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="bulk-email-preview" onClick={() => run(true)} disabled={busy} className={btnGhost}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Preview
          </button>
          <button data-testid="bulk-email-apply" onClick={() => run(false)} disabled={busy} className={btnPrimary}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Rebind
          </button>
        </div>
        {preview && (
          <div data-testid="bulk-email-preview-out" className="text-xs text-zinc-400 border border-zinc-800 rounded-sm p-3">
            {preview.ok === false ? preview.message : <>Would rebind <b className="text-zinc-200">{preview.rules_rebound}</b> rule(s) + <b className="text-zinc-200">{preview.catch_alls_rebound}</b> catch-all(s) across {preview.sites_scanned} zone(s) → <span className="mono">{preview.target}</span></>}
          </div>
        )}
        {result && (
          <div data-testid="bulk-email-result" className="text-xs text-emerald-400 border border-emerald-800 rounded-sm p-3 break-words">
            ✓ Rebound {result.rules_rebound} rule(s) + {result.catch_alls_rebound} catch-all(s) across {result.sites_scanned} zone(s) → <span className="mono break-all">{result.target}</span>
          </div>
        )}
        {err && <div data-testid="bulk-email-error" className="text-xs text-rose-400">{err}</div>}
      </div>
    </Modal>
  );
}

// ------------------------------------------------- Shuffle distinct looks
export function ShuffleLooksModal({ siteIds, onClose, onDone }) {
  const [assignments, setAssignments] = useState(null);
  const [busy, setBusy] = useState(false);
  const [applied, setApplied] = useState(null);
  const [err, setErr] = useState("");

  async function shuffle() {
    setBusy(true); setErr(""); setApplied(null);
    try {
      const r = await api.post("/bulk/shuffle-looks", { site_ids: siteIds, dry_run: true });
      setAssignments(r.data.assignments || []);
    } catch (e) { setErr(e?.response?.data?.detail || "Shuffle failed"); }
    finally { setBusy(false); }
  }
  // Shuffle once when the modal opens; `shuffle` reads only stable props.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { shuffle(); }, []);

  async function apply() {
    setBusy(true); setErr("");
    try {
      const r = await api.post("/bulk/shuffle-looks", { site_ids: siteIds, dry_run: false });
      setApplied(r.data);
      await onDone();
    } catch (e) { setErr(e?.response?.data?.detail || "Apply failed"); }
    finally { setBusy(false); }
  }

  return (
    <Modal
      testId="bulk-shuffle-modal"
      title="Shuffle distinct looks"
      subtitle={`Each of the ${siteIds.length} selected site${siteIds.length === 1 ? "" : "s"} gets its OWN unique palette-safe identity — no two identical (anti-footprint).`}
      onClose={onClose}
    >
      <div className="space-y-3">
        {assignments && !applied && (
          <div data-testid="bulk-shuffle-preview" className="border border-zinc-800 rounded-sm divide-y divide-zinc-800 max-h-72 overflow-y-auto">
            {assignments.map((a) => (
              <div key={a.site_id} className="flex items-center gap-3 px-3 py-2 text-xs" data-testid={`bulk-shuffle-row-${a.site_id}`}>
                <span className="inline-flex h-5 w-9 rounded-sm overflow-hidden border border-zinc-700 shrink-0">
                  <span className="flex-1" style={{ background: a.palette_swatch?.bg }} />
                  <span className="flex-1" style={{ background: a.palette_swatch?.accent }} />
                  <span className="flex-1" style={{ background: a.palette_swatch?.text }} />
                </span>
                <span className="truncate text-zinc-200 flex-1">{a.name}</span>
                <span className="text-zinc-500 truncate max-w-[55%] text-right">
                  {a.palette_label} · {a.look?.template}/{a.look?.variant} · {a.look?.header_variant} · {a.font_label}
                </span>
              </div>
            ))}
          </div>
        )}
        {applied && (
          <div data-testid="bulk-shuffle-applied" className="text-xs text-emerald-400 border border-emerald-800 rounded-sm p-3">
            ✓ Applied distinct looks to {applied.count} site(s). Refresh each public site to see its new identity.
          </div>
        )}
        <div className="flex items-center gap-2">
          <button data-testid="bulk-shuffle-reshuffle" onClick={shuffle} disabled={busy} className={btnGhost}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Shuffle className="w-3.5 h-3.5" />} Re-shuffle
          </button>
          <button data-testid="bulk-shuffle-apply" onClick={apply} disabled={busy || !assignments?.length || !!applied} className={btnPrimary}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Apply distinct looks
          </button>
        </div>
        {err && <div data-testid="bulk-shuffle-error" className="text-xs text-rose-400">{err}</div>}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------- Head script / tracking
export function HeadScriptModal({ siteIds, onClose, onDone }) {
  const [snippet, setSnippet] = useState("");
  const [mode, setMode] = useState("append");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [applied, setApplied] = useState(null);
  const [err, setErr] = useState("");

  async function run(dry) {
    if (!snippet.trim()) { setErr("Paste a script / snippet first"); return; }
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/bulk/head-script", { site_ids: siteIds, snippet, mode, dry_run: dry });
      if (dry) setPreview(data);
      else { setApplied(data); onDone?.(); }
    } catch (e) { setErr(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  }

  const counts = (rows) => {
    const c = { append: 0, replace: 0, skip: 0, error: 0 };
    (rows || []).forEach((t) => { c[t.action] = (c[t.action] || 0) + 1; });
    return c;
  };

  return (
    <Modal title="Add head script / tracking" subtitle={`Inject into <head> on ${siteIds.length} site(s)`} onClose={onClose} testId="bulk-head-modal">
      <div className="space-y-4">
        <div>
          <div className="label-tiny mb-2">Snippet (JS / pixel / meta / verification)</div>
          <textarea
            data-testid="bulk-head-snippet"
            value={snippet}
            onChange={(e) => { setSnippet(e.target.value); setPreview(null); setApplied(null); }}
            rows={6}
            placeholder={'<script defer src="https://cdn.example.com/a.js"></script>'}
            className={`${inputCls} mono text-xs`}
          />
        </div>
        <div className="flex items-center gap-4 text-sm">
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input data-testid="bulk-head-mode-append" type="radio" checked={mode === "append"} onChange={() => setMode("append")} /> Append (keep existing, skip dupes)
          </label>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input data-testid="bulk-head-mode-replace" type="radio" checked={mode === "replace"} onChange={() => setMode("replace")} /> Replace all
          </label>
        </div>

        {preview && (
          <div data-testid="bulk-head-preview" className="text-xs text-zinc-400 border border-zinc-800 rounded-sm p-3 break-words">
            {(() => { const c = counts(preview.targets); return `Will ${mode} on ${(c.append || 0) + (c.replace || 0)} site(s) · skip ${c.skip || 0} already-present${c.error ? ` · ${c.error} over cap` : ""}`; })()}
          </div>
        )}
        {applied && (
          <div data-testid="bulk-head-applied" className="text-xs text-emerald-400 border border-emerald-800 rounded-sm p-3 break-words">
            ✓ Updated {applied.modified} site(s) ({mode})
          </div>
        )}

        <div className="flex items-center gap-2 justify-end pt-2 border-t border-zinc-800">
          <button data-testid="bulk-head-preview-btn" onClick={() => run(true)} disabled={busy || !snippet.trim()} className={btnGhost}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Preview
          </button>
          <button data-testid="bulk-head-apply-btn" onClick={() => run(false)} disabled={busy || !snippet.trim() || !!applied} className={btnPrimary}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Apply to {siteIds.length}
          </button>
        </div>
        {err && <div data-testid="bulk-head-error" className="text-xs text-rose-400">{err}</div>}
      </div>
    </Modal>
  );
}

// ------------------------------------------- Lead form snippet (static sites)
export function FormSnippetModal({ siteIds, onClose, onDone }) {
  const [snippet, setSnippet] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [applied, setApplied] = useState(null);
  const [err, setErr] = useState("");

  async function run(dry) {
    if (!snippet.trim()) { setErr("Paste the form snippet first"); return; }
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/bulk/form-snippet", { site_ids: siteIds, snippet, dry_run: dry });
      if (dry) setPreview(data);
      else { setApplied(data); onDone?.(); }
    } catch (e) { setErr(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  }
  const skipped = (rows) => (rows || []).filter((t) => t.action === "skip");
  const rows = (applied || preview)?.results || (applied || preview)?.targets || [];

  return (
    <Modal title="Set lead form snippet" subtitle={`Replaces the JS form in the home #offer block on ${siteIds.length} static site(s) — news sites are skipped`} onClose={onClose} testId="bulk-form-modal">
      <div className="space-y-4">
        <div>
          <div className="label-tiny mb-2">JS form snippet</div>
          <textarea
            data-testid="bulk-form-snippet"
            value={snippet}
            onChange={(e) => { setSnippet(e.target.value); setPreview(null); setApplied(null); }}
            rows={7}
            spellCheck={false}
            placeholder={'<script src="https://forms.example.com/loader.js" data-key="…"></script>\n<div id="loan-form"></div>'}
            className={`${inputCls} mono text-xs`}
          />
          <div className="flex justify-between text-[11px] mono mt-1">
            <span className="text-zinc-500">Rendered verbatim, replaces any existing snippet.</span>
            <span className={snippet.length > 45000 ? "text-rose-400" : "text-zinc-500"}>{snippet.length.toLocaleString()} / 50,000</span>
          </div>
        </div>

        {preview && !applied && (
          <div data-testid="bulk-form-preview" className="text-xs text-zinc-400 border border-zinc-800 rounded-sm p-3 break-words">
            Will set on <b className="text-zinc-200">{preview.eligible}</b> of {preview.count} site(s){skipped(preview.targets).length ? ` · skip ${skipped(preview.targets).length}` : ""}
          </div>
        )}
        {applied && (
          <div data-testid="bulk-form-applied" className="text-xs text-emerald-400 border border-emerald-800 rounded-sm p-3 break-words">
            ✓ Updated {applied.modified} site(s) — home pages re-snapshotted and edge cache purged
          </div>
        )}
        <ResultLog results={rows.map((r) => ({ ...r, ok: r.action !== "skip", reason: r.action === "set" ? (r.had_snippet ? "replaced" : "set") : r.reason }))} />

        <div className="flex items-center gap-2 justify-end pt-2 border-t border-zinc-800">
          <button data-testid="bulk-form-preview-btn" onClick={() => run(true)} disabled={busy || !snippet.trim()} className={btnGhost}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Preview
          </button>
          <button data-testid="bulk-form-apply-btn" onClick={() => run(false)} disabled={busy || !snippet.trim() || !!applied} className={btnPrimary}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Apply to {siteIds.length}
          </button>
        </div>
        {err && <div data-testid="bulk-form-error" className="text-xs text-rose-400">{err}</div>}
      </div>
    </Modal>
  );
}

// ------------------------------------------- Root file (verification / ads.txt / favicon)
export function RootFileModal({ siteIds, onClose, onDone }) {
  const [mode, setMode] = useState("text");
  const [name, setName] = useState("");
  const [content, setContent] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [applied, setApplied] = useState(null);
  const [err, setErr] = useState("");
  const ready = name.trim() && (mode === "text" ? content.trim() : !!file);

  async function run(dry) {
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("site_ids", JSON.stringify(siteIds));
      fd.append("name", name.trim());
      if (mode === "text") fd.append("content", content); else fd.append("file", file);
      fd.append("dry_run", dry ? "true" : "false");
      const { data } = await api.post("/bulk/root-file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (dry) setPreview(data);
      else { setApplied(data); onDone?.(); }
    } catch (e) { setErr(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  }
  const rows = (applied?.results || []).map((r) => ({ ...r, ok: r.action === "set", reason: r.action === "set" ? (r.replaced ? "replaced" : "added") : r.error }));

  return (
    <Modal title="Add root file" subtitle={`Serve the same file at https://<domain>/<name> on ${siteIds.length} site(s)`} onClose={onClose} testId="bulk-root-file-modal">
      <div className="space-y-3">
        <div>
          <div className="label-tiny mb-1">Filename</div>
          <input data-testid="bulk-root-file-name" value={name} onChange={(e) => { setName(e.target.value); setPreview(null); setApplied(null); }}
                 placeholder="ads.txt · BingSiteAuth.xml · favicon.ico · .well-known/security.txt" className={`${inputCls} mono`} />
        </div>
        <div className="flex items-center gap-4 text-sm">
          <label className="flex items-center gap-1.5 cursor-pointer"><input data-testid="bulk-root-file-mode-text" type="radio" checked={mode === "text"} onChange={() => setMode("text")} /> Paste text</label>
          <label className="flex items-center gap-1.5 cursor-pointer"><input data-testid="bulk-root-file-mode-upload" type="radio" checked={mode === "upload"} onChange={() => setMode("upload")} /> Upload a file</label>
        </div>
        {mode === "text" ? (
          <textarea data-testid="bulk-root-file-content" value={content} onChange={(e) => { setContent(e.target.value); setPreview(null); setApplied(null); }} rows={5} spellCheck={false}
                    placeholder="google.com, pub-0000000000000000, DIRECT, f08c47fec0942fa0" className={`${inputCls} mono text-xs`} />
        ) : (
          <input data-testid="bulk-root-file-input" type="file" onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); setApplied(null); }} className="text-xs text-zinc-300" />
        )}
        <div className="text-[11px] text-zinc-500">Text/HTML/XML/JSON/JS/CSS/PDF/images, max 2 MB, 25 files per site. Existing files with the same name are replaced. <span className="text-amber-300/80">.php is refused — every /*.php URL is already the email-open pixel relay (Site → Settings).</span></div>

        {preview && !applied && (
          <div data-testid="bulk-root-file-preview" className="text-xs text-zinc-400 border border-zinc-800 rounded-sm p-3 break-words">
            Will store <span className="mono text-zinc-200">/{preview.name}</span> ({preview.content_type.split(";")[0]}, {preview.size} B) on {preview.count} site(s)
          </div>
        )}
        {applied && (
          <div data-testid="bulk-root-file-applied" className="text-xs text-emerald-400 border border-emerald-800 rounded-sm p-3 break-words">
            ✓ Stored /{applied.name} on {applied.modified} of {applied.count} site(s)
          </div>
        )}
        <ResultLog results={rows} />

        <div className="flex items-center gap-2 justify-end pt-2 border-t border-zinc-800">
          <button data-testid="bulk-root-file-preview-btn" onClick={() => run(true)} disabled={busy || !ready} className={btnGhost}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Preview
          </button>
          <button data-testid="bulk-root-file-apply-btn" onClick={() => run(false)} disabled={busy || !ready || !!applied} className={btnPrimary}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Apply to {siteIds.length}
          </button>
        </div>
        {err && <div data-testid="bulk-root-file-error" className="text-xs text-rose-400">{err}</div>}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------- Custom Cloudflare DNS
export function DnsRecordModal({ siteIds, onClose, onDone }) {
  const [type, setType] = useState("TXT");
  const [name, setName] = useState("@");
  const [content, setContent] = useState("");
  const [ttl, setTtl] = useState(1);
  const [proxied, setProxied] = useState(false);
  const [priority, setPriority] = useState(10);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [applied, setApplied] = useState(null);
  const [err, setErr] = useState("");

  const canProxy = ["A", "AAAA", "CNAME"].includes(type);
  const isMx = type === "MX";

  async function run(dry) {
    if (!content.trim()) { setErr("Enter the record content / value"); return; }
    setBusy(true); setErr("");
    try {
      const payload = { site_ids: siteIds, type, name: name.trim() || "@", content: content.trim(),
        ttl: Number(ttl) || 1, proxied: canProxy ? proxied : false, priority: isMx ? Number(priority) || 10 : null, dry_run: dry };
      const { data } = await api.post("/bulk/dns-record", payload);
      if (dry) setPreview(data);
      else { setApplied(data); onDone?.(); }
    } catch (e) { setErr(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  }

  return (
    <Modal title="Add Cloudflare DNS record" subtitle={`Create across ${siteIds.length} site(s)' zones`} onClose={onClose} testId="bulk-dns-modal">
      <div className="space-y-3">
        <div className="grid grid-cols-3 gap-2">
          <div>
            <div className="label-tiny mb-1">Type</div>
            <select data-testid="bulk-dns-type" value={type} onChange={(e) => { setType(e.target.value); setPreview(null); }} className={inputCls}>
              {["TXT", "CNAME", "A", "AAAA", "MX"].map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <div className="label-tiny mb-1">Name</div>
            <input data-testid="bulk-dns-name" value={name} onChange={(e) => { setName(e.target.value); setPreview(null); }} placeholder="@ or sub" className={`${inputCls} mono`} />
          </div>
          <div>
            <div className="label-tiny mb-1">TTL (secs)</div>
            <input data-testid="bulk-dns-ttl" type="number" value={proxied ? 1 : ttl} disabled={proxied} onChange={(e) => setTtl(e.target.value)} className={`${inputCls} mono disabled:opacity-40`} />
          </div>
        </div>
        <div>
          <div className="label-tiny mb-1">{isMx ? "Mail server (content)" : "Content / value"}</div>
          <input data-testid="bulk-dns-content" value={content} onChange={(e) => { setContent(e.target.value); setPreview(null); }} placeholder={type === "TXT" ? "google-site-verification=..." : type === "CNAME" ? "target.example.com" : type === "MX" ? "mx.provider.com" : "1.2.3.4"} className={`${inputCls} mono`} />
        </div>
        <div className="flex items-center gap-4">
          {isMx && (
            <div>
              <div className="label-tiny mb-1">Priority</div>
              <input data-testid="bulk-dns-priority" type="number" value={priority} onChange={(e) => setPriority(e.target.value)} className={`${inputCls} mono w-24`} />
            </div>
          )}
          {canProxy && (
            <label className="flex items-center gap-2 text-sm cursor-pointer mt-4">
              <input data-testid="bulk-dns-proxied" type="checkbox" checked={proxied} onChange={(e) => setProxied(e.target.checked)} />
              Cloudflare proxy (orange cloud)
            </label>
          )}
        </div>
        <div className="text-[11px] text-zinc-500">
          <span className="mono">@</span> = root domain; relative names auto-append the domain. Identical records are skipped; otherwise added alongside (never overwritten).
        </div>

        {preview && (
          <div data-testid="bulk-dns-preview" className="text-xs text-zinc-400 border border-zinc-800 rounded-sm p-3 break-words">
            Will create <span className="mono text-zinc-200">{preview.record?.type} {name || "@"} → {preview.record?.content}</span> on {(preview.targets || []).filter((t) => t.action === "create").length} zone(s){(preview.targets || []).some((t) => t.reason) ? ` · ${(preview.targets || []).filter((t) => t.reason).length} skipped (no zone)` : ""}
          </div>
        )}
        {applied && (
          <div data-testid="bulk-dns-applied" className="text-xs text-emerald-400 border border-emerald-800 rounded-sm p-3 break-words">
            ✓ Created {applied.created} record(s) across {applied.count} site(s)
          </div>
        )}

        <div className="flex items-center gap-2 justify-end pt-2 border-t border-zinc-800">
          <button data-testid="bulk-dns-preview-btn" onClick={() => run(true)} disabled={busy || !content.trim()} className={btnGhost}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Preview
          </button>
          <button data-testid="bulk-dns-apply-btn" onClick={() => run(false)} disabled={busy || !content.trim() || !!applied} className={btnPrimary}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Create on {siteIds.length}
          </button>
        </div>
        {err && <div data-testid="bulk-dns-error" className="text-xs text-rose-400">{err}</div>}
      </div>
    </Modal>
  );
}


// ---------------------------------------------- Google Postmaster TXT
export function PostmasterTxtModal({ siteIds, onClose, onDone }) {
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [applied, setApplied] = useState(null);
  const [err, setErr] = useState("");

  async function run(dry) {
    if (!token.trim()) { setErr("Paste the Google verification token"); return; }
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/bulk/postmaster-txt", { site_ids: siteIds, token: token.trim(), dry_run: dry });
      if (dry) setPreview(data);
      else { setApplied(data); onDone?.(); }
    } catch (e) { setErr(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  }

  return (
    <Modal title="Add Google Postmaster TXT" subtitle={`Write the verification TXT to ${siteIds.length} site(s)' apex`} onClose={onClose} testId="bulk-postmaster-modal">
      <div className="space-y-3">
        <div>
          <div className="label-tiny mb-1">Google verification token</div>
          <input data-testid="bulk-postmaster-token" value={token} onChange={(e) => { setToken(e.target.value); setPreview(null); }} placeholder="google-site-verification=xxxx  (or just the token)" className={`${inputCls} mono`} />
          <div className="text-[11px] text-zinc-500 mt-1">Paste exactly what Google shows — the <span className="mono">google-site-verification=</span> prefix is added automatically if you omit it. Written as a <span className="mono">TXT</span> at the root (<span className="mono">@</span>) of each zone. Existing TXT records (SPF/DKIM) are untouched; identical records are skipped.</div>
        </div>

        {preview && (
          <div data-testid="bulk-postmaster-preview" className="text-xs text-zinc-400 border border-zinc-800 rounded-sm p-3 break-words">
            Will write <span className="mono text-zinc-200">TXT @ → {preview.content}</span> on {(preview.targets || []).filter((t) => t.action === "create").length} zone(s){(preview.targets || []).some((t) => t.reason) ? ` · ${(preview.targets || []).filter((t) => t.reason).length} skipped (no zone)` : ""}
          </div>
        )}
        {applied && (
          <>
            <div data-testid="bulk-postmaster-applied" className="text-xs text-emerald-400 border border-emerald-800 rounded-sm p-3 break-words">
              ✓ Wrote TXT to {applied.created} zone(s) across {applied.count} site(s). Now open Google Postmaster Tools and click <b>Verify</b> on each domain (allow a few minutes for DNS to propagate).
            </div>
            <ResultLog results={(applied.results || []).map((r) => ({ ...r, ok: r.action !== "error", reason: r.action === "created" ? "TXT created" : r.reason }))} />
          </>
        )}

        <div className="flex items-center gap-2 justify-end pt-2 border-t border-zinc-800">
          <button data-testid="bulk-postmaster-preview-btn" onClick={() => run(true)} disabled={busy || !token.trim()} className={btnGhost}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Preview
          </button>
          <button data-testid="bulk-postmaster-apply-btn" onClick={() => run(false)} disabled={busy || !token.trim() || !!applied} className={btnPrimary}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Write to {siteIds.length}
          </button>
        </div>
        {err && <div data-testid="bulk-postmaster-error" className="text-xs text-rose-400">{err}</div>}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------- Safe Sources Pack
export function SafeSourcesModal({ siteIds, onClose, onDone }) {
  const [disableRisky, setDisableRisky] = useState(true);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [applied, setApplied] = useState(null);
  const [err, setErr] = useState("");
  const all = !siteIds?.length;

  async function run(dry) {
    setBusy(true); setErr("");
    try {
      const r = await api.post("/bulk/safe-sources", { site_ids: siteIds, disable_risky: disableRisky, dry_run: dry });
      if (dry) { setPreview(r.data); setApplied(null); }
      else { setApplied(r.data); setPreview(null); await onDone(); }
    } catch (e) { setErr(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  }
  const data = applied || preview;
  return (
    <Modal testId="bulk-safe-sources-modal" title="Safe Sources Pack" subtitle={all ? "Whole network — every site" : `${siteIds.length} site${siteIds.length === 1 ? "" : "s"} selected`} onClose={onClose}>
      <p className="text-xs text-zinc-400 leading-relaxed">
        Adds public-domain regulator/statistics feeds (SEC, Fed, BLS, BEA, Census, CFPB, FTC, CFTC, OCC, DOL, SBA), newswire press releases (PR Newswire, GlobeNewswire) and VOA to each site. Idempotent — feeds already present are skipped or re-enabled.
      </p>
      <label className="mt-3 flex items-center gap-2 text-sm text-zinc-200 cursor-pointer">
        <input type="checkbox" data-testid="bulk-safe-disable-risky" checked={disableRisky} onChange={(e) => setDisableRisky(e.target.checked)} />
        Disable copyrighted publisher feeds (CNBC, Yahoo, MarketWatch, BBC, Guardian, NYT, Investing.com…)
      </label>
      {data && (
        <div data-testid="bulk-safe-summary" className="mt-3 text-xs mono text-zinc-300 border border-zinc-800 rounded-sm px-3 py-2">
          {applied ? "Applied" : "Preview"} · {data.count} site(s) · add {data.total_added} · re-enable {data.total_re_enabled} · disable {data.total_disabled} · pack {data.pack_size} feeds
        </div>
      )}
      <ResultLog results={(data?.results || []).map((r) => ({ ...r, reason: `+${r.added} · re-on ${r.re_enabled} · off ${r.disabled}` }))} />
      {err && <div className="mt-3 text-xs text-rose-400">{err}</div>}
      <div className="mt-4 flex gap-2">
        <button onClick={() => run(true)} disabled={busy} data-testid="bulk-safe-preview" className={btnGhost}>{busy && !preview ? <Loader2 size={12} className="animate-spin" /> : null} Preview</button>
        <button onClick={() => run(false)} disabled={busy || !preview} data-testid="bulk-safe-apply" className={btnPrimary}>{busy && preview ? <Loader2 size={12} className="animate-spin" /> : null} Apply to {all ? "all sites" : `${siteIds.length} site(s)`}</button>
      </div>
    </Modal>
  );
}
